import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Chip,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TablePagination,
  InputAdornment,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import promptService from '../services/promptService';
import NewPrompt from './NewPrompt';
import ReactMarkdown from 'react-markdown';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prompt-settings-tabpanel-${index}`}
      aria-labelledby={`prompt-settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const FragmentForm = ({ fragment, onSave, onCancel }) => {
  const [content, setContent] = useState(fragment?.content || '');
  const [tags, setTags] = useState(fragment?.tags?.join(', ') || '');
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleSubmit = () => {
    onSave({
      ...fragment,
      content,
      tags: tags.split(',').map(tag => tag.trim()).filter(Boolean)
    });
  };

  const handleCopyContent = () => {
    navigator.clipboard.writeText(content);
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          {fragment ? 'Modifica Frammento' : 'Nuovo Frammento'}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button startIcon={<VisibilityIcon />} onClick={handlePreview}>
            Anteprima
          </Button>
          <Button startIcon={<CopyIcon />} onClick={handleCopyContent}>
            Copia del frammento
          </Button>
          <Button onClick={onCancel}>Annulla</Button>
        </Stack>
      </Box>
      
      <Paper sx={{ p: 3 }}>
        <TextField
          fullWidth
          label="Contenuto del frammento"
          multiline
          rows={4}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Tag (separati da virgola)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="es. introduzione, stile, contenuto"
        />
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!content.trim()}
          >
            {fragment ? 'Aggiorna' : 'Crea'}
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Anteprima Frammento
          <IconButton
            aria-label="close"
            onClick={handleClosePreview}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <ReactMarkdown>{content}</ReactMarkdown>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const PromptSettings = () => {
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState(0);
  const [fragments, setFragments] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [currentFragment, setCurrentFragment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uniqueCodexTypes, setUniqueCodexTypes] = useState([]);
  const [fragmentsLoading, setFragmentsLoading] = useState(true);
  const [fragmentsError, setFragmentsError] = useState(null);
  const [activePage, setActivePage] = useState('list');
  const [editPrompt, setEditPrompt] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [errorDialog, setErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  
  // Load data on initialization
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setFragmentsLoading(true);
        
        // Carica prompts e fragment in parallelo
        const [promptsData, fragmentsData] = await Promise.all([
          promptService.getAll(),
          promptService.getAllFragments()
        ]);

        setPrompts(promptsData);
        setFragments(fragmentsData);
        
        // Extract unique types for filtering
        const types = [...new Set(promptsData.map(codex => codex.codexType?.code || ''))].filter(Boolean);
        setUniqueCodexTypes(types);
        
        // Check if we're returning from fragment creation/editing
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('tab') === 'fragments') {
          setCurrentTab(1); // Switch to fragments tab
        }
        
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data");
        setFragmentsError("Failed to load fragments");
      } finally {
        setLoading(false);
        setFragmentsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleChangeTab = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleCreateFragment = () => {
    navigate('/new-prompt-fragment');
  };

  const handleUpdateFragment = async (fragment) => {
    try {
      const updatedFragment = await promptService.updateFragment(fragment.id, fragment);
      setFragments(fragments.map(f => f.id === fragment.id ? updatedFragment : f));
      setActivePage('list');
    } catch (error) {
      console.error('Error updating fragment:', error);
      setFragmentsError('Failed to update fragment');
    }
  };

  const handleCreatePrompt = () => {
    navigate('/new-prompt');
  };

  const handleDeletePrompt = async (promptId) => {
    const prompt = prompts.find(p => p.id === promptId);
    setDeleteItemId(promptId);
    setDeleteItemName(prompt.code);
    setDialogType('prompt');
    setOpenDialog(true);
  };

  const handleDeleteFragment = async (fragmentId) => {
    const fragment = fragments.find(f => f.id === fragmentId);
    setDeleteItemId(fragmentId);
    setDeleteItemName(fragment.code);
    setDialogType('fragment');
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (dialogType === 'prompt') {
        await promptService.delete(deleteItemId);
        setPrompts(prompts.filter(p => p.id !== deleteItemId));
      } else {
        await promptService.deleteFragment(deleteItemId);
        setFragments(fragments.filter(f => f.id !== deleteItemId));
      }
    } catch (error) {
      console.error(`Error deleting ${dialogType}:`, error);
      if (error.response?.status === 409) {
        setErrorMessage('Impossibile eliminare il fragment: è associato ad uno o più prompt.');
        setErrorDialog(true);
      } else {
        setError(`Failed to delete ${dialogType}`);
      }
    } finally {
      setOpenDialog(false);
      setDeleteItemId(null);
      setDeleteItemName('');
      setDialogType('');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDeleteItemId(null);
    setDeleteItemName('');
    setDialogType('');
  };

  const handleCloseErrorDialog = () => {
    setErrorDialog(false);
    setErrorMessage('');
  };

  const handleEditPrompt = (prompt) => {
    navigate(`/prompts/${prompt.id}`);
  };

  const handleEditFragment = (fragment) => {
    navigate(`/prompt-fragments/${fragment.id}`);
  };

   // Toggle queue active state
   const handleToggleActive = async (prompt) => {
    try {
      const updatedPrompt = await promptService.toggleStatus(prompt.id);
      setPrompts(prompts.map(p => p.id === prompt.id ? updatedPrompt : p));
    } catch (error) {
      console.error('Error toggling prompt active state:', error);
      setError('Failed to update prompt status');
    }
  };

  const handleCloseEditDialog = () => {
    setEditPrompt(null);
    setOpenEditDialog(false);
  };

  const handleSavePrompt = async (updatedPrompt) => {
    try {
      await promptService.update(updatedPrompt.id, updatedPrompt);
      setPrompts(prompts.map(p => p.id === updatedPrompt.id ? updatedPrompt : p));
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating prompt:', error);
      setError('Failed to update prompt');
    }
  };

  const renderPromptList = () => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Cerca per codice o descrizione..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControl size="small" sx={{ ml: 1, minWidth: 120 }}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Tipo"
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {uniqueCodexTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreatePrompt}
            >
              Aggiungi
            </Button>
          </Grid>
        </Grid>
      </Box>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
              <TableCell>Codice</TableCell>
              <TableCell>Descrizione</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell align="center">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prompts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nessun prompt trovato
                </TableCell>
              </TableRow>
            ) : (
              prompts
                .filter(prompt => {
                  const matchesSearch = !searchTerm || 
                    prompt.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    prompt.description.toLowerCase().includes(searchTerm.toLowerCase());
                  const matchesType = !typeFilter || prompt.codexType.code === typeFilter;
                  return matchesSearch && matchesType;
                })
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(prompt => (
                  <TableRow 
                    key={prompt.id}
                    hover
                  >
                    <TableCell>
                      <Typography variant="subtitle2">{prompt.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {prompt.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={prompt.codexType.code}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={prompt.isEnabled}
                              onChange={() => handleToggleActive(prompt)}
                              color="primary"
                            />
                          }
                          label=""
                          sx={{ mr: 1 }}
                        />
                        <IconButton 
                          onClick={() => handleEditPrompt(prompt)}
                          size="small"
                          title="Modifica prompt"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeletePrompt(prompt.id)}
                          size="small"
                          color="error"
                          title="Elimina prompt"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={prompts.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );

  const renderFragmentList = () => (
    <Box>
      {fragmentsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {fragmentsError}
        </Alert>
      )}

      {fragmentsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Cerca frammenti..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControl size="small" sx={{ ml: 1, minWidth: 120 }}>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={selectedTag}
                      onChange={(e) => setSelectedTag(e.target.value)}
                    >
                      <MenuItem value="">Tutti</MenuItem>
                      {Array.from(new Set(fragments.map(f => f.type?.id).filter(Boolean))).map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateFragment}
                >
                  Aggiungi
                </Button>
              </Grid>
            </Grid>
          </Box>
          
          {(!fragments || fragments.length === 0) ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography>Nessun frammento creato. Crea il tuo primo frammento!</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableCell>Codice</TableCell>
                    <TableCell>Descrizione</TableCell>
                    <TableCell>Contenuto</TableCell>
                    <TableCell align="center">Lunghezza</TableCell>
                    <TableCell align="center">Tipo</TableCell>
                    <TableCell align="center">Prompts</TableCell>
                    <TableCell align="center">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(fragments || [])
                    .filter(fragment => {
                      const matchesSearch = searchTerm === '' || 
                        fragment.content.toLowerCase().includes(searchTerm.toLowerCase());
                      const matchesTag = selectedTag === '' || 
                        fragment.type?.id === selectedTag;
                      return matchesSearch && matchesTag;
                    })
                    .map(fragment => {
                      const contentPreview = fragment.content.length > 50 
                        ? `${fragment.content.substring(0, 50)}...` 
                        : fragment.content;
                      
                      return (
                        <TableRow key={fragment.id} hover>
                          <TableCell>
                            <Typography variant="subtitle2">{fragment.code}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {fragment.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {contentPreview}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {fragment.content.length}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Chip
                                  key={fragment.type.id}
                                  label={fragment.type.id}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ mr: 0.5 }}
                                />
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                          {fragment.promptCount}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <IconButton 
                                onClick={() => handleEditFragment(fragment)}
                                size="small"
                                title="Modifica frammento"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton 
                                onClick={() => handleDeleteFragment(fragment.id)}
                                size="small"
                                color="error"
                                title="Elimina frammento"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );

  const renderContent = () => {
    switch (activePage) {
      case 'create-fragment':
        return <FragmentForm onSave={handleCreateFragment} onCancel={() => setActivePage('list')} />;
      
      case 'edit-fragment':
        return <FragmentForm 
          fragment={currentFragment} 
          onSave={handleUpdateFragment} 
          onCancel={() => setActivePage('list')} 
        />;
      
      default:
        return (
          <Paper elevation={0} sx={{ mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={currentTab} onChange={handleChangeTab} aria-label="prompt settings tabs">
                <Tab label="Prompts" id="prompt-settings-tab-0" aria-controls="prompt-settings-tabpanel-0" />
                <Tab label="Libreria Frammenti" id="prompt-settings-tab-1" aria-controls="prompt-settings-tabpanel-1" />
              </Tabs>
            </Box>
            <TabPanel value={currentTab} index={0}>
              {renderPromptList()}
            </TabPanel>
            <TabPanel value={currentTab} index={1}>
              {renderFragmentList()}
            </TabPanel>
          </Paper>
        );
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestione Prompts</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : (
        renderContent()
      )}

      <Dialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <NewPrompt
            prompt={editPrompt}
            onSave={handleSavePrompt}
            onCancel={handleCloseEditDialog}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Conferma eliminazione
        </DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare {dialogType === 'prompt' ? 'il prompt' : 'il frammento'} <strong>{deleteItemName}</strong>?
            {dialogType === 'fragment' && ' Questa azione rimuoverà il frammento da tutti i prompt che lo utilizzano.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={errorDialog}
        onClose={handleCloseErrorDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Errore
        </DialogTitle>
        <DialogContent>
          <Typography>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} color="primary">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default PromptSettings; 